import { DeleteOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, SendOutlined, SyncOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Descriptions, Form, Input, message, Modal, Select, Space, Table, Tag, Upload } from 'antd';
import axios from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { userService } from '../services/userService';
import { configService } from '../services/configService';
import { messageService } from '../services/messageService';
import { ZaloTemplateCreateParam, ZaloTemplateDetail, ZaloTemplateItem } from '../types/message';
import '../styles/AdminPages.css';

const { Option } = Select;
const { TextArea } = Input;

interface RecipientOption {
    uid: string;
    label: string;
    source: 'firebase' | 'zalo';
}

type SendMode = 'production' | 'development';
type RecipientType = 'uid' | 'phone';
type StatusFilter = 'all' | 1 | 2 | 3 | 4;

const RECIPIENT_OPTIONS_CACHE_TTL = 5 * 60 * 1000;

const ZaloTemplateManagement: React.FC = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState<ZaloTemplateItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [total, setTotal] = useState(0);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createForm] = Form.useForm();

    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedTemplateDetail, setSelectedTemplateDetail] = useState<ZaloTemplateDetail | null>(null);
    const [uploadingField, setUploadingField] = useState<string | null>(null);
    const [checkingPermission, setCheckingPermission] = useState(false);
    const [sendModalVisible, setSendModalVisible] = useState(false);
    const [sendTemplateLoading, setSendTemplateLoading] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [sendTemplateDetail, setSendTemplateDetail] = useState<ZaloTemplateDetail | null>(null);
    const [recipientOptions, setRecipientOptions] = useState<RecipientOption[]>([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [sendForm] = Form.useForm();
    const isMountedRef = useRef(true);
    const templateListRequestIdRef = useRef(0);
    const templateListInFlightRef = useRef<Map<StatusFilter, Promise<void>>>(new Map());
    const templateDetailCacheRef = useRef<Map<string, ZaloTemplateDetail>>(new Map());
    const templateDetailInFlightRef = useRef<Map<string, Promise<ZaloTemplateDetail>>>(new Map());
    const recipientOptionsCacheRef = useRef<{ data: RecipientOption[]; expiresAt: number } | null>(null);
    const recipientOptionsInFlightRef = useRef<Promise<RecipientOption[]> | null>(null);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const normalizePhoneForZalo = (rawPhone?: string): string => {
        const normalized = String(rawPhone || '').trim().replace(/[\s.+()-]/g, '');
        if (!normalized) return '';
        if (normalized.startsWith('84')) return normalized;
        if (normalized.startsWith('0')) return `84${normalized.slice(1)}`;
        return normalized;
    };

    const generateTrackingId = (length = 64) => {
        const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const randomValues = new Uint32Array(length);
        window.crypto.getRandomValues(randomValues);
        return Array.from(randomValues, (value) => charset[value % charset.length]).join('');
    };

    const setDefaultCreateFormValues = useCallback(() => {
        createForm.setFieldsValue({
            template_type: 1,
            tag: 1,
            tracking_id: generateTrackingId(),
            header_type: 'IMAGE',
            body_title: '',
            body_paragraph: '',
            table_rows: [],
            buttons: [],
            params: [],
            note: ''
        });
    }, [createForm]);

    const parseTemplateCreatedTime = (createdTime: number) => {
        if (!createdTime) return '-';
        const milliseconds = createdTime > 1_000_000_000_000 ? createdTime : createdTime * 1000;
        return moment(milliseconds).format('DD/MM/YYYY HH:mm:ss');
    };

    const getStatusTagColor = (status: string) => {
        switch (status) {
            case 'ENABLE':
                return 'green';
            case 'PENDING_REVIEW':
                return 'gold';
            case 'REJECT':
                return 'red';
            case 'DISABLE':
                return 'default';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status?: string) => {
        switch (status) {
            case 'ENABLE':
                return 'Đang hoạt động';
            case 'PENDING_REVIEW':
                return 'Chờ duyệt';
            case 'REJECT':
                return 'Bị từ chối';
            case 'DISABLE':
                return 'Ngừng hoạt động';
            case 'DELETE':
                return 'Đã xóa';
            default:
                return status || 'Không xác định';
        }
    };

    const getQualityTagColor = (quality?: string | null) => {
        switch (quality) {
            case 'HIGH':
                return 'green';
            case 'MEDIUM':
                return 'blue';
            case 'LOW':
                return 'red';
            default:
                return 'default';
        }
    };

    const getQualityLabel = (quality?: string | null) => {
        switch (quality) {
            case 'HIGH':
                return 'Cao';
            case 'MEDIUM':
                return 'Trung bình';
            case 'LOW':
                return 'Thấp';
            case 'UNDEFINED':
                return 'Chưa đánh giá';
            default:
                return 'Chưa có';
        }
    };

    const getTemplateTagLabel = (tag?: string) => {
        switch (tag) {
            case 'TRANSACTION':
            case 'IN_TRANSACTION':
                return 'Giao dịch';
            case 'CUSTOMER_CARE':
                return 'Chăm sóc khách hàng';
            case 'PROMOTION':
                return 'Hậu mãi';
            default:
                return tag || 'Không xác định';
        }
    };

    const parseTemplateTimeout = (timeout?: number) => {
        if (!timeout) return '-';
        const totalSeconds = Math.floor(timeout / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        return `${hours} giờ ${minutes} phút`;
    };

    const formatPrice = (price?: string) => {
        if (!price) return '-';
        const numericPrice = Number(price);
        if (Number.isNaN(numericPrice)) return price;
        return `${numericPrice.toLocaleString('vi-VN')} đ`;
    };

    const getButtonTypeLabel = (type: number) => {
        switch (type) {
            case 1: return 'Đến trang doanh nghiệp/hoá đơn';
            case 2: return 'Gọi điện';
            case 3: return 'Đến trang thông tin OA';
            case 4: return 'Đến Zalo Mini App doanh nghiệp';
            case 5: return 'Đến trang tải ứng dụng';
            case 6: return 'Đến trang phân phối sản phẩm';
            case 7: return 'Đến trang web/Zalo Mini App khác';
            case 8: return 'Đến ứng dụng khác';
            case 9: return 'Đến bài viết OA';
            case 10: return 'Sao chép';
            case 11: return 'Thanh toán ngay';
            case 12: return 'Xem chi tiết';
            default: return 'Không xác định';
        }
    };

    const getTagOptionsByTemplateType = (templateType: 1 | 2 | 3 | 4 | 5) => {
        switch (templateType) {
            case 1:
                return [1, 2, 3];
            case 2:
            case 3:
                return [1];
            case 4:
                return [1, 2, 3];
            case 5:
                return [2];
            default:
                return [1];
        }
    };

    const getTemplateTypeLabel = (type: number) => {
        switch (type) {
            case 1:
                return 'Template tùy chỉnh';
            case 2:
                return 'Template xác thực';
            case 3:
                return 'Template yêu cầu thanh toán';
            case 4:
                return 'Template voucher';
            case 5:
                return 'Template đánh giá dịch vụ';
            default:
                return 'Không xác định';
        }
    };

    const getTagLabelFromValue = (tagValue?: number) => {
        switch (tagValue) {
            case 1:
                return 'Giao dịch';
            case 2:
                return 'Chăm sóc khách hàng';
            case 3:
                return 'Hậu mãi';
            default:
                return 'Không xác định';
        }
    };

    const fetchZaloTemplates = useCallback(async (status: StatusFilter = 'all', force = false) => {
        if (!force) {
            const currentInFlight = templateListInFlightRef.current.get(status);
            if (currentInFlight) {
                return currentInFlight;
            }
        }

        const requestId = ++templateListRequestIdRef.current;

        const requestPromise = (async () => {
            setLoading(true);
            try {
                const response = await messageService.getZaloTemplateList({
                    offset: 0,
                    limit: 100,
                    status: status === 'all' ? undefined : status,
                    filterPreset: 1
                });

                if (!isMountedRef.current || requestId !== templateListRequestIdRef.current) {
                    return;
                }

                setTemplates(response.data || []);
                setTotal(response.metadata?.total || 0);
            } catch (error: any) {
                if (!isMountedRef.current || requestId !== templateListRequestIdRef.current) {
                    return;
                }
                console.error('Error fetching Zalo templates:', error);
                message.error(error?.message || 'Không thể tải danh sách template từ Zalo');
            } finally {
                templateListInFlightRef.current.delete(status);
                if (isMountedRef.current && requestId === templateListRequestIdRef.current) {
                    setLoading(false);
                }
            }
        })();

        templateListInFlightRef.current.set(status, requestPromise);
        return requestPromise;
    }, []);

    const fetchTemplateDetailById = useCallback(async (templateId: string, force = false): Promise<ZaloTemplateDetail> => {
        if (!force) {
            const cachedDetail = templateDetailCacheRef.current.get(templateId);
            if (cachedDetail) {
                return cachedDetail;
            }

            const currentInFlight = templateDetailInFlightRef.current.get(templateId);
            if (currentInFlight) {
                return currentInFlight;
            }
        }

        const requestPromise = messageService
            .getZaloTemplateDetail(templateId)
            .then((response) => {
                templateDetailCacheRef.current.set(templateId, response.data);
                return response.data;
            })
            .finally(() => {
                templateDetailInFlightRef.current.delete(templateId);
            });

        templateDetailInFlightRef.current.set(templateId, requestPromise);
        return requestPromise;
    }, []);

    const handleViewTemplateDetail = async (templateId: string) => {
        setDetailModalVisible(true);
        setDetailLoading(true);
        setSelectedTemplateDetail(null);
        try {
            const detail = await fetchTemplateDetailById(templateId);
            if (!isMountedRef.current) return;
            setSelectedTemplateDetail(detail);
        } catch (error: any) {
            console.error('Error fetching template detail:', error);
            message.error(error?.message || 'Không thể tải chi tiết template');
            setDetailModalVisible(false);
        } finally {
            if (isMountedRef.current) {
                setDetailLoading(false);
            }
        }
    };

    const handleCreateTemplate = async (values: any) => {
        if (uploadingField) {
            message.warning('Đang upload ảnh, vui lòng đợi hoàn tất trước khi tạo template');
            return;
        }

        setCreateLoading(true);
        message.loading({ content: 'Đang tạo template...', key: 'zalo-template-create-modal', duration: 0 });
        try {
            const templateType = Number(values.template_type) as 1 | 2 | 3 | 4 | 5;
            const tag = Number(values.tag) as 1 | 2 | 3;
            const allowedTags = getTagOptionsByTemplateType(templateType);
            if (!allowedTags.includes(tag)) {
                message.error({ content: 'Tag không hợp lệ với loại template đã chọn', key: 'zalo-template-create-modal' });
                setCreateLoading(false);
                return;
            }

            const headerComponents: any[] = [];
            if (values.header_type === 'IMAGE' && values.header_image_media_id?.trim()) {
                headerComponents.push({
                    IMAGES: {
                        items: [
                            {
                                type: 'IMAGE',
                                media_id: values.header_image_media_id.trim()
                            }
                        ]
                    }
                });
            }
            if (
                values.header_type === 'LOGO' &&
                values.header_logo_light_media_id?.trim() &&
                values.header_logo_dark_media_id?.trim()
            ) {
                headerComponents.push({
                    LOGO: {
                        light: {
                            type: 'IMAGE',
                            media_id: values.header_logo_light_media_id.trim()
                        },
                        dark: {
                            type: 'IMAGE',
                            media_id: values.header_logo_dark_media_id.trim()
                        }
                    }
                });
            }

            const bodyComponents: any[] = [];
            if (values.body_title?.trim()) {
                bodyComponents.push({
                    TITLE: {
                        value: values.body_title.trim()
                    }
                });
            }
            if (values.body_paragraph?.trim()) {
                bodyComponents.push({
                    PARAGRAPH: {
                        value: values.body_paragraph.trim()
                    }
                });
            }

            const tableRows = (values.table_rows || [])
                .filter((row: any) => row?.title?.trim() && row?.value?.trim())
                .map((row: any) => ({
                    title: row.title.trim(),
                    value: row.value.trim(),
                    ...(row.row_type ? { row_type: Number(row.row_type) } : {})
                }));

            if (tableRows.length > 0) {
                bodyComponents.push({
                    TABLE: {
                        rows: tableRows
                    }
                });
            }

            const footerButtons = (values.buttons || [])
                .filter((button: any) => button?.type && button?.title?.trim() && button?.content?.trim())
                .map((button: any) => ({
                    type: Number(button.type),
                    title: button.title.trim(),
                    content: button.content.trim()
                }));

            const footerComponents = footerButtons.length > 0
                ? [
                    {
                        BUTTONS: {
                            items: footerButtons
                        }
                    }
                ]
                : [];

            const layout = {
                header: {
                    components: headerComponents
                },
                body: {
                    components: bodyComponents
                },
                footer: {
                    components: footerComponents
                }
            };

            const params = (values.params || [])
                .filter((param: any) => param?.type && param?.name?.trim() && param?.sample_value?.trim())
                .map((param: any) => ({
                    type: String(param.type),
                    name: param.name.trim(),
                    sample_value: param.sample_value.trim()
                })) as ZaloTemplateCreateParam[];

            await messageService.createZaloTemplate({
                template_name: values.template_name?.trim(),
                template_type: templateType,
                tag,
                layout,
                ...(params.length > 0 ? { params } : {}),
                ...(values.note?.trim() ? { note: values.note.trim() } : {}),
                tracking_id: values.tracking_id?.trim()
            });

            message.success({ content: 'Tạo template thành công, trạng thái hiện tại là Chờ duyệt', key: 'zalo-template-create-modal' });
            setCreateModalVisible(false);
            createForm.resetFields();
            await fetchZaloTemplates(statusFilter, true);
        } catch (error: any) {
            console.error('Error creating template:', error);
            message.error({ content: error?.message || 'Không thể tạo template mới', key: 'zalo-template-create-modal' });
        } finally {
            setCreateLoading(false);
        }
    };

    const handleUploadMedia = async (file: File, fieldName: string) => {
        setUploadingField(fieldName);
        message.loading({ content: 'Đang upload ảnh...', key: 'zalo-template-upload-modal', duration: 0 });
        try {
            const mediaId = await messageService.uploadZaloTemplateImage(file);
            createForm.setFieldValue(fieldName, mediaId);
            message.success({ content: 'Upload ảnh thành công, đã điền media ID', key: 'zalo-template-upload-modal' });
        } catch (error: any) {
            console.error('Error uploading image to Zalo:', error);
            message.error({ content: error?.message || 'Không thể upload ảnh', key: 'zalo-template-upload-modal' });
        } finally {
            setUploadingField(null);
        }
        return false;
    };

    const handleCheckPermission = async () => {
        setCheckingPermission(true);
        message.loading({ content: 'Đang kiểm tra quyền OA...', key: 'zalo-template-check-permission-list', duration: 0 });
        try {
            const response = await messageService.checkZaloTemplatePermission();
            message.success({ content: 'OA đã có quyền sử dụng template resource', key: 'zalo-template-check-permission-list' });
            Modal.info({
                title: 'Kết quả kiểm tra quyền OA',
                width: 720,
                content: (
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                        {JSON.stringify(response, null, 2)}
                    </pre>
                )
            });
        } catch (error: any) {
            message.error({ content: error?.message || 'Kiểm tra quyền OA thất bại', key: 'zalo-template-check-permission-list' });
        } finally {
            setCheckingPermission(false);
        }
    };

    const openSendTemplateModal = async (template: ZaloTemplateItem) => {
        if (template.status !== 'ENABLE') {
            message.warning('Chỉ có thể gửi thử với template đã được duyệt và đang hoạt động');
            return;
        }

        setSendModalVisible(true);
        setSendTemplateLoading(true);
        setSendTemplateDetail(null);
        sendForm.resetFields();

        try {
            const [templateDetail] = await Promise.all([
                fetchTemplateDetailById(template.templateId),
                loadRecipientOptions()
            ]);
            if (!isMountedRef.current) return;
            setSendTemplateDetail(templateDetail);
            sendForm.setFieldsValue({
                send_mode: 'production',
                recipient_type: 'uid',
                user_id: '',
                phone: '',
                tracking_id: generateTrackingId(),
                template_data: {}
            });
        } catch (error: any) {
            console.error('Error fetching template detail for sending:', error);
            message.error(error?.message || 'Không thể tải thông tin template để gửi tin');
            setSendModalVisible(false);
        } finally {
            if (isMountedRef.current) {
                setSendTemplateLoading(false);
            }
        }
    };

    const loadRecipientOptions = useCallback(async (force = false): Promise<RecipientOption[]> => {
        const now = Date.now();

        if (!force) {
            const cached = recipientOptionsCacheRef.current;
            if (cached && cached.expiresAt > now) {
                setRecipientOptions(cached.data);
                return cached.data;
            }

            if (recipientOptionsInFlightRef.current) {
                const inFlightResult = await recipientOptionsInFlightRef.current;
                if (isMountedRef.current) {
                    setRecipientOptions(inFlightResult);
                }
                return inFlightResult;
            }
        }

        setLoadingRecipients(true);
        const requestPromise = (async () => {
            const firebaseUsers = await userService.getAllUsers();

            const mergedByUid = new Map<string, RecipientOption>();

            firebaseUsers.forEach((user: any) => {
                const uid = String(user?.zaloUserId || '').trim();
                if (!uid) return;
                const name = (user?.name || '').trim();
                const phone = (user?.phoneNumber || '').trim();
                const displayParts = [name || 'Không tên', phone ? `SĐT: ${phone}` : null].filter(Boolean);
                mergedByUid.set(uid, {
                    uid,
                    label: `${displayParts.join(' - ')} [Firebase]`,
                    source: 'firebase'
                });
            });

            let accessToken = '';
            try {
                const config = await configService.getConfig();
                accessToken = (config?.access_token_zalo || '').trim();
            } catch {
                accessToken = '';
            }

            if (accessToken) {
                let offset = 0;
                const count = 50;
                let hasMore = true;

                while (hasMore) {
                    const response = await axios.get(
                        `https://openapi.zalo.me/v3.0/oa/user/getlist?data=${JSON.stringify({ offset, count })}`,
                        {
                            headers: {
                                access_token: accessToken
                            }
                        }
                    );

                    if (response?.data?.error !== 0) {
                        throw new Error(response?.data?.message || 'Không lấy được danh sách follower từ Zalo');
                    }

                    const users = response?.data?.data?.users || [];
                    users.forEach((item: any) => {
                        const uid = String(item?.user_id || '').trim();
                        if (!uid) return;

                        if (!mergedByUid.has(uid)) {
                            mergedByUid.set(uid, {
                                uid,
                                label: `${uid} [Zalo OA]`,
                                source: 'zalo'
                            });
                        }
                    });

                    if (users.length < count) {
                        hasMore = false;
                    } else {
                        offset += count;
                    }
                }
            }

            const options = Array.from(mergedByUid.values()).sort((a, b) => {
                if (a.source !== b.source) {
                    return a.source === 'firebase' ? -1 : 1;
                }
                return a.label.localeCompare(b.label, 'vi');
            });

            recipientOptionsCacheRef.current = {
                data: options,
                expiresAt: Date.now() + RECIPIENT_OPTIONS_CACHE_TTL
            };

            if (isMountedRef.current) {
                setRecipientOptions(options);
            }
            return options;
        })();

        recipientOptionsInFlightRef.current = requestPromise;

        try {
            return await requestPromise;
        } catch (error: any) {
            console.error('Error loading recipient options:', error);
            message.error(error?.message || 'Không thể tải đầy đủ danh sách UID');
            return [];
        } finally {
            recipientOptionsInFlightRef.current = null;
            if (isMountedRef.current) {
                setLoadingRecipients(false);
            }
        }
    }, []);

    const handleSendTemplateMessage = async (values: any) => {
        if (!sendTemplateDetail) return;

        setSendLoading(true);
        message.loading({ content: 'Đang gửi tin nhắn template...', key: 'zalo-template-send', duration: 0 });

        try {
            const sendMode = (values?.send_mode || 'production') as SendMode;
            const recipientType = (values?.recipient_type || 'uid') as RecipientType;
            const payloadTemplateData = (sendTemplateDetail.listParams || []).reduce<Record<string, string>>((acc, param) => {
                const rawValue = values?.template_data?.[param.name];
                if (typeof rawValue === 'string' && rawValue.trim()) {
                    acc[param.name] = rawValue.trim();
                }
                return acc;
            }, {});

            const payload: any = {
                template_id: sendTemplateDetail.templateId,
                template_data: payloadTemplateData,
                ...(values.tracking_id?.trim() ? { tracking_id: values.tracking_id.trim() } : {})
            };

            if (sendMode === 'development' || recipientType === 'phone') {
                const normalizedPhone = normalizePhoneForZalo(values.phone);
                if (!normalizedPhone) {
                    throw new Error('Vui lòng nhập số điện thoại người nhận');
                }
                payload.phone = normalizedPhone;
            } else {
                if (!values.user_id?.trim()) {
                    throw new Error('Vui lòng chọn UID người dùng Zalo');
                }
                payload.recipient = { user_id: values.user_id.trim() };
            }

            const response = sendMode === 'development'
                ? await messageService.sendZBSTemplateMessageDevelopment(payload)
                : await messageService.sendZBSTemplateMessage(payload);

            const sentMessageId = response?.data?.message_id;
            message.success({
                content: sentMessageId
                    ? `Gửi tin thành công. message_id: ${sentMessageId}`
                    : 'Gửi tin thành công',
                key: 'zalo-template-send'
            });

            setSendModalVisible(false);
            setSendTemplateDetail(null);
            sendForm.resetFields();
        } catch (error: any) {
            console.error('Error sending template message:', error);
            message.error({ content: error?.message || 'Không thể gửi tin template', key: 'zalo-template-send' });
        } finally {
            setSendLoading(false);
        }
    };

    const buildPreviewData = (values: any) => {
        const tableRows = (values.table_rows || [])
            .filter((row: any) => row?.title?.trim() && row?.value?.trim())
            .map((row: any) => ({
                title: row.title.trim(),
                value: row.value.trim()
            }));

        const buttons = (values.buttons || [])
            .filter((button: any) => button?.title?.trim() && button?.content?.trim())
            .map((button: any) => ({
                type: Number(button.type),
                title: button.title.trim(),
                content: button.content.trim()
            }));

        return {
            templateName: values.template_name?.trim(),
            tagLabel: getTagLabelFromValue(Number(values.tag)),
            headerType: values.header_type,
            headerImageMediaId: values.header_image_media_id?.trim(),
            headerLogoLightMediaId: values.header_logo_light_media_id?.trim(),
            headerLogoDarkMediaId: values.header_logo_dark_media_id?.trim(),
            bodyTitle: values.body_title?.trim(),
            bodyParagraph: values.body_paragraph?.trim(),
            tableRows,
            buttons
        };
    };

    const watchedCreateValues = Form.useWatch([], createForm);
    const livePreviewData = buildPreviewData(watchedCreateValues || {});

    const renderPreviewParagraph = (text?: string) => {
        if (!text) return null;
        return text.split(/(<[^>]+>)/g).map((part: string, index: number) => {
            if (/^<[^>]+>$/.test(part)) {
                return (
                    <span key={`${part}-${index}`} style={{ fontWeight: 600, color: '#1d39c4' }}>
                        {part}
                    </span>
                );
            }
            return <span key={`${part}-${index}`}>{part}</span>;
        });
    };

    useEffect(() => {
        fetchZaloTemplates('all');
        setDefaultCreateFormValues();
    }, [fetchZaloTemplates, setDefaultCreateFormValues]);

    const templateColumns = [
        { title: 'Mã mẫu', dataIndex: 'templateId', key: 'templateId', width: 140 },
        { title: 'Tên mẫu', dataIndex: 'templateName', key: 'templateName' },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: 170,
            render: (status: string) => <Tag color={getStatusTagColor(status)}>{getStatusLabel(status)}</Tag>
        },
        {
            title: 'Chất lượng',
            dataIndex: 'templateQuality',
            key: 'templateQuality',
            width: 130,
            render: (quality: string) => <Tag color={getQualityTagColor(quality)}>{getQualityLabel(quality)}</Tag>
        },
        {
            title: 'Thời gian tạo',
            dataIndex: 'createdTime',
            key: 'createdTime',
            width: 190,
            render: (createdTime: number) => parseTemplateCreatedTime(createdTime)
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 140,
            render: (_: any, record: ZaloTemplateItem) => (
                <Space size={4} direction="vertical">
                    <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewTemplateDetail(record.templateId)}>
                        Xem chi tiết
                    </Button>
                    <Button
                        type="link"
                        icon={<SendOutlined />}
                        disabled={record.status !== 'ENABLE'}
                        onClick={() => openSendTemplateModal(record)}
                    >
                        Gửi template
                    </Button>
                </Space>
            )
        }
    ];

    const templateParamColumns = useMemo(() => [
        { title: 'Tên tham số', dataIndex: 'name', key: 'name' },
        { title: 'Bắt buộc', dataIndex: 'require', key: 'require', width: 100, render: (required: boolean) => <Tag color={required ? 'red' : 'default'}>{required ? 'Có' : 'Không'}</Tag> },
        { title: 'Kiểu', dataIndex: 'type', key: 'type', width: 120 },
        { title: 'Độ dài tối thiểu', dataIndex: 'minLength', key: 'minLength', width: 140 },
        { title: 'Độ dài tối đa', dataIndex: 'maxLength', key: 'maxLength', width: 140 },
        { title: 'Cho phép rỗng', dataIndex: 'acceptNull', key: 'acceptNull', width: 120, render: (acceptNull: boolean) => <Tag color={acceptNull ? 'blue' : 'default'}>{acceptNull ? 'Có' : 'Không'}</Tag> }
    ], []);

    const templateButtonColumns = useMemo(() => [
        { title: 'Loại nút', dataIndex: 'type', key: 'type', width: 280, render: (type: number) => <span>{type} - {getButtonTypeLabel(type)}</span> },
        { title: 'Tiêu đề', dataIndex: 'title', key: 'title', width: 220 },
        { title: 'Nội dung', dataIndex: 'content', key: 'content' }
    ], []);

    const recipientSelectOptions = useMemo(
        () => recipientOptions.map((item) => ({
            value: item.uid,
            label: item.label
        })),
        [recipientOptions]
    );

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1>Quản lý template Zalo</h1>
                    <Tag color="blue">{total} mẫu tin</Tag>
                </div>
                <div className="admin-page-actions">
                    <Space>
                        <Button loading={checkingPermission} onClick={handleCheckPermission}>
                            Kiểm tra quyền OA
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => navigate('/zalo-templates/create')}
                        >
                            Tạo template mới
                        </Button>
                        <Select
                            value={statusFilter}
                            style={{ width: 220 }}
                            onChange={(value: StatusFilter) => setStatusFilter(value)}
                        >
                            <Option value="all">Tất cả trạng thái</Option>
                            <Option value={1}>Đang hoạt động</Option>
                            <Option value={2}>Chờ duyệt</Option>
                            <Option value={3}>Bị từ chối</Option>
                            <Option value={4}>Ngừng hoạt động</Option>
                        </Select>
                        <Button type="primary" icon={<ReloadOutlined />} onClick={() => fetchZaloTemplates(statusFilter, true)} loading={loading}>
                            Làm mới danh sách
                        </Button>
                    </Space>
                </div>
            </div>

            <div className="admin-form-section">
                <div className="admin-table-container">
                    <Table
                        className="admin-table"
                        columns={templateColumns}
                        dataSource={templates}
                        rowKey={(record) => record.templateId}
                        loading={loading}
                        scroll={{ x: 1000 }}
                        pagination={{ defaultPageSize: 10, position: ['bottomRight'] }}
                    />
                </div>
            </div>

            <Modal
                title="Chi tiết mẫu tin Zalo"
                visible={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedTemplateDetail(null);
                }}
                footer={null}
                width={1000}
                className="admin-modal"
                destroyOnClose={true}
            >
                <div className="admin-form-container">
                    <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="Mã mẫu">{selectedTemplateDetail?.templateId || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Tên mẫu">{selectedTemplateDetail?.templateName || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Trạng thái">
                            {selectedTemplateDetail?.status ? <Tag color={getStatusTagColor(selectedTemplateDetail.status)}>{getStatusLabel(selectedTemplateDetail.status)}</Tag> : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mức chất lượng">
                            {selectedTemplateDetail?.templateQuality ? <Tag color={getQualityTagColor(selectedTemplateDetail.templateQuality)}>{getQualityLabel(selectedTemplateDetail.templateQuality)}</Tag> : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Nhóm nội dung">{getTemplateTagLabel(selectedTemplateDetail?.templateTag)}</Descriptions.Item>
                        <Descriptions.Item label="Thời gian hết hạn">{parseTemplateTimeout(selectedTemplateDetail?.timeout)}</Descriptions.Item>
                        <Descriptions.Item label="Đơn giá gửi qua SĐT">{formatPrice(selectedTemplateDetail?.price_sdt)}</Descriptions.Item>
                        <Descriptions.Item label="Đơn giá gửi qua UID">{formatPrice(selectedTemplateDetail?.price_uid)}</Descriptions.Item>
                        <Descriptions.Item label="Link xem trước" span={2}>
                            {selectedTemplateDetail?.previewUrl ? (
                                <a href={selectedTemplateDetail.previewUrl} target="_blank" rel="noreferrer">{selectedTemplateDetail.previewUrl}</a>
                            ) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ghi chú trạng thái" span={2}>{selectedTemplateDetail?.reason || '-'}</Descriptions.Item>
                    </Descriptions>

                    <div className="admin-form-section">
                        <h3 className="admin-form-section-title">Danh sách tham số</h3>
                        <Table
                            className="admin-table"
                            columns={templateParamColumns}
                            dataSource={selectedTemplateDetail?.listParams || []}
                            rowKey={(record) => record.name}
                            loading={detailLoading}
                            pagination={false}
                            scroll={{ x: 800 }}
                        />
                    </div>

                    <div className="admin-form-section">
                        <h3 className="admin-form-section-title">Danh sách nút kêu gọi hành động (CTA)</h3>
                        <Table
                            className="admin-table"
                            columns={templateButtonColumns}
                            dataSource={selectedTemplateDetail?.listButtons || []}
                            rowKey={(record, index) => `${record.type}-${index}`}
                            loading={detailLoading}
                            pagination={false}
                            scroll={{ x: 900 }}
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                title="Gửi template (Production / Development)"
                visible={sendModalVisible}
                onCancel={() => {
                    if (sendLoading) return;
                    setSendModalVisible(false);
                    setSendTemplateDetail(null);
                    sendForm.resetFields();
                }}
                footer={null}
                width={760}
                className="admin-modal"
                destroyOnClose={true}
            >
                <div className="admin-form-container">
                    <Form
                        form={sendForm}
                        layout="vertical"
                        onFinish={handleSendTemplateMessage}
                    >
                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Chế độ gửi</h3>
                            <Space style={{ width: '100%' }} size={12} wrap>
                                <Form.Item
                                    name="send_mode"
                                    label="API gửi"
                                    initialValue="production"
                                    rules={[{ required: true, message: 'Vui lòng chọn API gửi' }]}
                                    style={{ minWidth: 260, flex: 1 }}
                                >
                                    <Select
                                        disabled={sendTemplateLoading || sendLoading}
                                        onChange={(mode: SendMode) => {
                                            if (mode === 'development') {
                                                sendForm.setFieldsValue({ recipient_type: 'phone', user_id: '' });
                                            }
                                        }}
                                    >
                                        <Option value="production">API 1 - Gửi thật (`/message/template`)</Option>
                                        <Option value="development">API 2 - Test dev (`/message/template/development`)</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    shouldUpdate={(prev, next) => prev.send_mode !== next.send_mode}
                                    noStyle
                                >
                                    {() => {
                                        const sendMode = (sendForm.getFieldValue('send_mode') || 'production') as SendMode;
                                        if (sendMode === 'development') {
                                            return null;
                                        }
                                        return (
                                            <Form.Item
                                                name="recipient_type"
                                                label="Kiểu người nhận"
                                                initialValue="uid"
                                                rules={[{ required: true, message: 'Vui lòng chọn kiểu người nhận' }]}
                                                style={{ minWidth: 240, flex: 1 }}
                                            >
                                                <Select
                                                    disabled={sendTemplateLoading || sendLoading}
                                                    onChange={(type: RecipientType) => {
                                                        if (type === 'uid') {
                                                            sendForm.setFieldValue('phone', '');
                                                        } else {
                                                            sendForm.setFieldValue('user_id', '');
                                                        }
                                                    }}
                                                >
                                                    <Option value="uid">UID follower</Option>
                                                    <Option value="phone">Số điện thoại</Option>
                                                </Select>
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                            </Space>
                        </div>

                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Thông tin người nhận</h3>
                            <Form.Item shouldUpdate={(prev, next) => prev.send_mode !== next.send_mode || prev.recipient_type !== next.recipient_type} noStyle>
                                {() => {
                                    const sendMode = (sendForm.getFieldValue('send_mode') || 'production') as SendMode;
                                    const recipientType = (sendForm.getFieldValue('recipient_type') || 'uid') as RecipientType;
                                    const usePhone = sendMode === 'development' || recipientType === 'phone';

                                    if (usePhone) {
                                        return (
                                            <Form.Item
                                                name="phone"
                                                label="Số điện thoại người nhận"
                                                rules={[
                                                    { required: true, message: 'Vui lòng nhập số điện thoại người nhận' },
                                                    {
                                                        validator: async (_, value) => {
                                                            const normalized = normalizePhoneForZalo(value);
                                                            if (!normalized) {
                                                                throw new Error('Số điện thoại không hợp lệ');
                                                            }
                                                            if (!/^84\d{8,11}$/.test(normalized)) {
                                                                throw new Error('Số điện thoại cần theo định dạng 84xxxxxxxxx');
                                                            }
                                                        }
                                                    }
                                                ]}
                                                extra={sendMode === 'development'
                                                    ? 'Development mode bắt buộc gửi theo số điện thoại trong danh sách test trên Zalo.'
                                                    : 'Ví dụ: 0986716147 hoặc 84986716147 (hệ thống sẽ chuẩn hoá về 84xxxxxxxxx).'}
                                            >
                                                <Input
                                                    placeholder="Nhập số điện thoại người nhận"
                                                    disabled={sendTemplateLoading || sendLoading}
                                                />
                                            </Form.Item>
                                        );
                                    }

                                    return (
                                        <>
                                            <Form.Item
                                                name="user_id"
                                                label="UID người dùng Zalo"
                                                rules={[
                                                    { required: true, message: 'Vui lòng chọn UID người dùng Zalo' },
                                                    { pattern: /^\d+$/, message: 'UID chỉ được chứa chữ số' }
                                                ]}
                                            >
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Chọn UID từ Firebase hoặc danh sách follower Zalo"
                                                    loading={loadingRecipients}
                                                    disabled={sendTemplateLoading || sendLoading}
                                                    optionFilterProp="label"
                                                    options={recipientSelectOptions}
                                                    notFoundContent={loadingRecipients ? 'Đang tải danh sách UID...' : 'Không có UID phù hợp'}
                                                />
                                            </Form.Item>
                                            <div style={{ marginTop: -8, marginBottom: 12, color: '#8c8c8c', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                                <span>Đang có {recipientOptions.length} UID từ Firebase + Zalo OA.</span>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    loading={loadingRecipients}
                                                    disabled={sendTemplateLoading || sendLoading}
                                                    onClick={() => loadRecipientOptions(true)}
                                                >
                                                    Làm mới UID
                                                </Button>
                                            </div>
                                        </>
                                    );
                                }}
                            </Form.Item>
                            <Form.Item
                                name="tracking_id"
                                label="Tracking ID (tuỳ chọn)"
                                rules={[{ max: 100, message: 'Tracking ID tối đa 100 ký tự' }]}
                            >
                                <Input
                                    placeholder="Hỗ trợ theo dõi trạng thái gửi"
                                    disabled={sendTemplateLoading || sendLoading}
                                    addonAfter={(
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<SyncOutlined />}
                                            disabled={sendTemplateLoading || sendLoading}
                                            onClick={() => sendForm.setFieldValue('tracking_id', generateTrackingId())}
                                            style={{ padding: 0, height: 'auto' }}
                                        >
                                            Random
                                        </Button>
                                    )}
                                />
                            </Form.Item>
                        </div>

                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Thông tin template</h3>
                            <Descriptions bordered size="small" column={1}>
                                <Descriptions.Item label="Mã template">{sendTemplateDetail?.templateId || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Tên template">{sendTemplateDetail?.templateName || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Trạng thái">
                                    {sendTemplateDetail?.status ? (
                                        <Tag color={getStatusTagColor(sendTemplateDetail.status)}>{getStatusLabel(sendTemplateDetail.status)}</Tag>
                                    ) : '-'}
                                </Descriptions.Item>
                            </Descriptions>
                        </div>

                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Dữ liệu động (template_data)</h3>
                            {(sendTemplateDetail?.listParams || []).length === 0 && (
                                <div style={{ color: '#8c8c8c' }}>Template này không có tham số động. Có thể gửi ngay.</div>
                            )}
                            {(sendTemplateDetail?.listParams || []).map((param) => (
                                <Form.Item
                                    key={param.name}
                                    name={['template_data', param.name]}
                                    label={`${param.name} (${param.type})`}
                                    rules={[
                                        ...(param.require ? [{ required: true, message: `Vui lòng nhập ${param.name}` }] : []),
                                        ...(param.minLength > 0 ? [{ min: param.minLength, message: `${param.name} tối thiểu ${param.minLength} ký tự` }] : []),
                                        ...(param.maxLength > 0 ? [{ max: param.maxLength, message: `${param.name} tối đa ${param.maxLength} ký tự` }] : [])
                                    ]}
                                    extra={`Bắt buộc: ${param.require ? 'Có' : 'Không'} | Cho phép rỗng: ${param.acceptNull ? 'Có' : 'Không'}`}
                                >
                                    <Input
                                        placeholder={`Nhập giá trị cho ${param.name}`}
                                        disabled={sendTemplateLoading || sendLoading}
                                    />
                                </Form.Item>
                            ))}
                        </div>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button
                                    onClick={() => {
                                        setSendModalVisible(false);
                                        setSendTemplateDetail(null);
                                        sendForm.resetFields();
                                    }}
                                    disabled={sendLoading}
                                >
                                    Hủy
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SendOutlined />}
                                    loading={sendLoading}
                                    disabled={sendTemplateLoading || (sendTemplateDetail?.status !== 'ENABLE')}
                                >
                                    Gửi tin nhắn
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </div>
            </Modal>

            <Modal
                title="Tạo template mới"
                visible={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                confirmLoading={createLoading}
                width={'100%'}
                style={{ top: 16 }}
                className="admin-modal"
                destroyOnClose={true}
                bodyStyle={{ height: '86vh', overflow: 'hidden', paddingTop: 12 }}
                footer={[
                    <Button key="cancel" onClick={() => setCreateModalVisible(false)}>
                        Hủy
                    </Button>,
                    <Button key="submit" type="primary" loading={createLoading || !!uploadingField} disabled={!!uploadingField} onClick={() => createForm.submit()}>
                        Tạo template
                    </Button>
                ]}
            >
                <div className="admin-form-container" style={{ display: 'flex', gap: 16, height: '100%', minHeight: 0 }}>
                    <div style={{ flex: 1.6, overflowY: 'auto', paddingRight: 10, minHeight: 0 }}>
                    <Form
                        form={createForm}
                        layout="vertical"
                        onFinish={handleCreateTemplate}
                    >
                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Thông tin cơ bản</h3>
                            <Form.Item
                                name="template_name"
                                label="Tên mẫu tin"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập tên mẫu tin' },
                                    { min: 10, message: 'Tên mẫu tin tối thiểu 10 ký tự' },
                                    { max: 60, message: 'Tên mẫu tin tối đa 60 ký tự' }
                                ]}
                            >
                                <Input placeholder="Ví dụ: Mẫu chăm sóc khách hàng tháng 03" />
                            </Form.Item>

                            <Space style={{ width: '100%' }} size={12} wrap>
                                <Form.Item
                                    name="template_type"
                                    label="Loại mẫu tin"
                                    rules={[{ required: true, message: 'Vui lòng chọn loại mẫu tin' }]}
                                    style={{ minWidth: 260, flex: 1 }}
                                >
                                    <Select
                                        onChange={(value: 1 | 2 | 3 | 4 | 5) => {
                                            const currentTag = createForm.getFieldValue('tag');
                                            const allowedTags = getTagOptionsByTemplateType(value);
                                            if (!allowedTags.includes(Number(currentTag))) {
                                                createForm.setFieldValue('tag', allowedTags[0]);
                                            }
                                        }}
                                    >
                                        <Option value={1}>{getTemplateTypeLabel(1)}</Option>
                                        <Option value={2}>{getTemplateTypeLabel(2)}</Option>
                                        <Option value={3}>{getTemplateTypeLabel(3)}</Option>
                                        <Option value={4}>{getTemplateTypeLabel(4)}</Option>
                                        <Option value={5}>{getTemplateTypeLabel(5)}</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    shouldUpdate={(prevValues, currentValues) => prevValues.template_type !== currentValues.template_type}
                                    noStyle
                                >
                                    {() => {
                                        const templateType = (createForm.getFieldValue('template_type') || 1) as 1 | 2 | 3 | 4 | 5;
                                        const allowedTags = getTagOptionsByTemplateType(templateType);
                                        return (
                                            <Form.Item
                                                name="tag"
                                                label="Nhóm nội dung (Tag)"
                                                rules={[{ required: true, message: 'Vui lòng chọn nhóm nội dung' }]}
                                                style={{ minWidth: 240, flex: 1 }}
                                            >
                                                <Select>
                                                    {allowedTags.includes(1) && <Option value={1}>Giao dịch</Option>}
                                                    {allowedTags.includes(2) && <Option value={2}>Chăm sóc khách hàng</Option>}
                                                    {allowedTags.includes(3) && <Option value={3}>Hậu mãi</Option>}
                                                </Select>
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                            </Space>

                            <Form.Item
                                name="tracking_id"
                                label="Tracking ID"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập tracking ID' },
                                    { max: 100, message: 'Tracking ID tối đa 100 ký tự' }
                                ]}
                            >
                                <Input
                                    placeholder="Hệ thống tự tạo ngẫu nhiên theo dạng token"
                                    addonAfter={(
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<SyncOutlined />}
                                            onClick={() => createForm.setFieldValue('tracking_id', generateTrackingId())}
                                            style={{ padding: 0, height: 'auto' }}
                                        >
                                            Random
                                        </Button>
                                    )}
                                />
                            </Form.Item>

                            <Form.Item
                                name="note"
                                label="Ghi chú kiểm duyệt"
                                rules={[{ max: 400, message: 'Ghi chú tối đa 400 ký tự' }]}
                            >
                                <TextArea
                                    rows={2}
                                    placeholder="Mô tả ngắn mục đích sử dụng template để đội ngũ kiểm duyệt dễ theo dõi"
                                />
                            </Form.Item>
                        </div>

                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Khai báo nội dung template</h3>
                            <Form.Item
                                name="header_type"
                                label="Kiểu phần đầu (Header)"
                                rules={[{ required: true, message: 'Vui lòng chọn kiểu header' }]}
                            >
                                <Select>
                                    <Option value="IMAGE">Ảnh</Option>
                                    <Option value="LOGO">Logo sáng/tối</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item shouldUpdate={(prev, next) => prev.header_type !== next.header_type} noStyle>
                                {() => {
                                    const headerType = createForm.getFieldValue('header_type');
                                    if (headerType === 'LOGO') {
                                        return (
                                            <Space style={{ width: '100%' }} size={16}>
                                                <Form.Item
                                                    name="header_logo_light_media_id"
                                                    label="Media ID logo sáng"
                                                    rules={[{ required: true, message: 'Vui lòng nhập media ID logo sáng' }]}
                                                    style={{ minWidth: 320, flex: 1 }}
                                                >
                                                    <Input
                                                        placeholder="Nhập media_id của logo light"
                                                        addonAfter={(
                                                            <Upload
                                                                showUploadList={false}
                                                                accept="image/*"
                                                                beforeUpload={(file) => handleUploadMedia(file as File, 'header_logo_light_media_id')}
                                                            >
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    icon={<UploadOutlined />}
                                                                    loading={uploadingField === 'header_logo_light_media_id'}
                                                                    style={{ padding: 0, height: 'auto' }}
                                                                >
                                                                    Tải ảnh
                                                                </Button>
                                                            </Upload>
                                                        )}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    name="header_logo_dark_media_id"
                                                    label="Media ID logo tối"
                                                    rules={[{ required: true, message: 'Vui lòng nhập media ID logo tối' }]}
                                                    style={{ minWidth: 320, flex: 1 }}
                                                >
                                                    <Input
                                                        placeholder="Nhập media_id của logo dark"
                                                        addonAfter={(
                                                            <Upload
                                                                showUploadList={false}
                                                                accept="image/*"
                                                                beforeUpload={(file) => handleUploadMedia(file as File, 'header_logo_dark_media_id')}
                                                            >
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    icon={<UploadOutlined />}
                                                                    loading={uploadingField === 'header_logo_dark_media_id'}
                                                                    style={{ padding: 0, height: 'auto' }}
                                                                >
                                                                    Tải ảnh
                                                                </Button>
                                                            </Upload>
                                                        )}
                                                    />
                                                </Form.Item>
                                            </Space>
                                        );
                                    }

                                    return (
                                        <Form.Item
                                            name="header_image_media_id"
                                            label="Media ID hình ảnh header"
                                            rules={[{ required: true, message: 'Vui lòng nhập media ID hình ảnh' }]}
                                        >
                                            <Input
                                                placeholder="Nhập media_id hình ảnh đã upload lên Zalo"
                                                addonAfter={(
                                                    <Upload
                                                        showUploadList={false}
                                                        accept="image/*"
                                                        beforeUpload={(file) => handleUploadMedia(file as File, 'header_image_media_id')}
                                                    >
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            icon={<UploadOutlined />}
                                                            loading={uploadingField === 'header_image_media_id'}
                                                            style={{ padding: 0, height: 'auto' }}
                                                        >
                                                            Tải ảnh
                                                        </Button>
                                                    </Upload>
                                                )}
                                            />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>

                            <div style={{ marginTop: -8, marginBottom: 12, color: '#8c8c8c' }}>
                                Bạn có thể bấm "Tải ảnh" để hệ thống upload ảnh lên Zalo và tự điền media ID.
                            </div>

                            <Form.Item
                                name="body_title"
                                label="Tiêu đề"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập tiêu đề' },
                                    { max: 65, message: 'Tiêu đề tối đa 65 ký tự' }
                                ]}
                            >
                                <Input placeholder="Ví dụ: Thông báo đơn hàng đã xác nhận" />
                            </Form.Item>

                            <Form.Item
                                name="body_paragraph"
                                label="Văn bản nội dung"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập văn bản nội dung' },
                                    { max: 400, message: 'Nội dung tối đa 400 ký tự' },
                                    {
                                        validator: async (_, value) => {
                                            const content = (value || '').toString();
                                            const requiredParams = ['<ten_khach_hang>', '<so_dien_thoai>'];
                                            const missingParams = requiredParams.filter((param) => !content.includes(param));

                                            if (missingParams.length > 0) {
                                                throw new Error(`Nội dung bắt buộc chứa: ${requiredParams.join(' và ')}`);
                                            }
                                        }
                                    }
                                ]}
                                extra="Bạn có thể dùng biến dạng <ten_khach_hang>, <ma_don_hang>..."
                            >
                                <TextArea rows={4} placeholder="Nhập nội dung chính của template" />
                            </Form.Item>

                            <Form.List name="table_rows">
                                {(fields, { add, remove }) => (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600 }}>Bảng dữ liệu (Table)</span>
                                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>
                                                Thêm dòng
                                            </Button>
                                        </div>
                                        {fields.map((field) => (
                                            <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                                                <Form.Item
                                                    name={[field.name, 'title']}
                                                    label="Nhãn"
                                                    style={{ width: 200 }}
                                                >
                                                    <Input placeholder="Ví dụ: Mã đơn" />
                                                </Form.Item>
                                                <Form.Item
                                                    name={[field.name, 'value']}
                                                    label="Giá trị"
                                                    style={{ width: 280 }}
                                                >
                                                    <Input placeholder="Ví dụ: <order_code>" />
                                                </Form.Item>
                                                <Form.Item
                                                    name={[field.name, 'row_type']}
                                                    label="Loại dòng"
                                                    style={{ width: 140 }}
                                                >
                                                    <Select allowClear placeholder="Mặc định">
                                                        <Option value={1}>Nhấn mạnh</Option>
                                                        <Option value={2}>Thường</Option>
                                                    </Select>
                                                </Form.Item>
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => remove(field.name)}
                                                    style={{ marginTop: 30 }}
                                                />
                                            </Space>
                                        ))}
                                    </div>
                                )}
                            </Form.List>

                            <Form.List name="buttons">
                                {(fields, { add, remove }) => (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600 }}>Nút thao tác (CTA)</span>
                                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>
                                                Thêm nút
                                            </Button>
                                        </div>
                                        {fields.map((field) => (
                                            <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <Form.Item
                                                    name={[field.name, 'type']}
                                                    style={{ width: 220, marginBottom: 0 }}
                                                    rules={[{ required: true, message: 'Chọn loại nút' }]}
                                                >
                                                    <Select placeholder="Loại nút">
                                                        <Option value={1}>1 - Đến trang doanh nghiệp</Option>
                                                        <Option value={2}>2 - Gọi điện</Option>
                                                        <Option value={3}>3 - Trang OA</Option>
                                                        <Option value={4}>4 - Zalo Mini App</Option>
                                                        <Option value={5}>5 - Tải ứng dụng</Option>
                                                        <Option value={6}>6 - Phân phối sản phẩm</Option>
                                                        <Option value={7}>7 - Trang web/ZMA khác</Option>
                                                        <Option value={8}>8 - Ứng dụng khác</Option>
                                                        <Option value={9}>9 - Bài viết OA</Option>
                                                        <Option value={10}>10 - Sao chép</Option>
                                                        <Option value={11}>11 - Thanh toán ngay</Option>
                                                        <Option value={12}>12 - Xem chi tiết</Option>
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item
                                                    name={[field.name, 'title']}
                                                    style={{ width: 220, marginBottom: 0 }}
                                                    rules={[{ required: true, message: 'Nhập tiêu đề nút' }]}
                                                >
                                                    <Input placeholder="Tiêu đề nút" />
                                                </Form.Item>
                                                <Form.Item
                                                    name={[field.name, 'content']}
                                                    style={{ flex: 1, marginBottom: 0 }}
                                                    rules={[{ required: true, message: 'Nhập nội dung nút' }]}
                                                >
                                                    <Input placeholder="URL hoặc số điện thoại theo loại nút" />
                                                </Form.Item>
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => remove(field.name)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.List>

                            <Form.List name="params">
                                {(fields, { add, remove }) => (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600 }}>Tham số động (Params)</span>
                                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>
                                                Thêm tham số
                                            </Button>
                                        </div>
                                        {fields.length === 0 && (
                                            <div style={{ marginBottom: 8, color: '#8c8c8c' }}>
                                                Không bắt buộc. Chỉ thêm khi trong nội dung có biến như &lt;ten_khach_hang&gt;.
                                            </div>
                                        )}
                                        {fields.map((field) => (
                                            <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <Form.Item
                                                    name={[field.name, 'name']}
                                                    style={{ width: 220, marginBottom: 0 }}
                                                    rules={[{ required: true, message: 'Nhập tên tham số' }]}
                                                >
                                                    <Input placeholder="Ví dụ: ten_khach_hang" />
                                                </Form.Item>
                                                <Form.Item
                                                    name={[field.name, 'type']}
                                                    style={{ width: 260, marginBottom: 0 }}
                                                    rules={[{ required: true, message: 'Chọn loại tham số' }]}
                                                >
                                                    <Select placeholder="Loại dữ liệu">
                                                        <Option value="1">1 - Tên khách hàng</Option>
                                                        <Option value="2">2 - Số điện thoại</Option>
                                                        <Option value="3">3 - Địa chỉ</Option>
                                                        <Option value="4">4 - Mã số</Option>
                                                        <Option value="5">5 - Nhãn tùy chỉnh</Option>
                                                        <Option value="6">6 - Trạng thái giao dịch</Option>
                                                        <Option value="7">7 - Thông tin liên hệ</Option>
                                                        <Option value="8">8 - Giới tính / Danh xưng</Option>
                                                        <Option value="9">9 - Tên sản phẩm / Thương hiệu</Option>
                                                        <Option value="10">10 - Số lượng / Số tiền</Option>
                                                        <Option value="11">11 - Thời gian</Option>
                                                        <Option value="12">12 - OTP</Option>
                                                        <Option value="13">13 - URL</Option>
                                                        <Option value="14">14 - Tiền tệ (VNĐ)</Option>
                                                        <Option value="15">15 - Bank transfer note</Option>
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item
                                                    name={[field.name, 'sample_value']}
                                                    style={{ flex: 1, marginBottom: 0 }}
                                                    rules={[{ required: true, message: 'Nhập dữ liệu mẫu' }]}
                                                >
                                                    <Input placeholder="Dữ liệu mẫu để kiểm duyệt" />
                                                </Form.Item>
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => remove(field.name)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.List>
                        </div>
                    </Form>
                    </div>
                    <div
                        style={{
                            width: 320,
                            borderLeft: '1px solid #f0f0f0',
                            paddingLeft: 12,
                            overflowY: 'auto',
                            minHeight: 0
                        }}
                    >
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>Xem trước Template</div>
                        <div
                            style={{
                                background: '#f0f2ff',
                                padding: 12,
                                borderRadius: 8,
                                border: '1px solid #d6dcff'
                            }}
                        >
                            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong style={{ fontSize: 14 }}>{livePreviewData?.templateName || 'Tên mẫu tin'}</strong>
                                <Tag color="blue">{livePreviewData?.tagLabel || 'Tag'}</Tag>
                            </div>

                            <div
                                style={{
                                    background: '#fff',
                                    borderRadius: 10,
                                    border: '1px solid #e8e8e8',
                                    padding: 14
                                }}
                            >
                                {livePreviewData?.headerType && (
                                    <div style={{ marginBottom: 10 }}>
                                        {livePreviewData.headerType === 'IMAGE' ? (
                                            <div style={{ background: '#fafafa', border: '1px dashed #d9d9d9', padding: 8, borderRadius: 6, fontSize: 11 }}>
                                                Header ảnh (media_id): {livePreviewData?.headerImageMediaId || '(chưa chọn)'}
                                            </div>
                                        ) : (
                                            <div style={{ background: '#fafafa', border: '1px dashed #d9d9d9', padding: 8, borderRadius: 6, fontSize: 11 }}>
                                                Logo sáng: {livePreviewData?.headerLogoLightMediaId || '(chưa chọn)'}
                                                <br />
                                                Logo tối: {livePreviewData?.headerLogoDarkMediaId || '(chưa chọn)'}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div style={{ fontSize: 24, color: '#1d39c4', fontWeight: 700, lineHeight: 1, marginBottom: 8 }}>Zalo</div>
                                <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>
                                    {livePreviewData?.bodyTitle || 'Tiêu đề template'}
                                </div>

                                <div style={{ marginBottom: 12, whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: 14 }}>
                                    {renderPreviewParagraph(livePreviewData?.bodyParagraph) || 'Nội dung sẽ hiển thị tại đây'}
                                </div>

                                {livePreviewData?.tableRows?.length > 0 && (
                                    <div style={{ marginBottom: 12 }}>
                                        {livePreviewData.tableRows.map((row: any, index: number) => (
                                            <div key={`${row.title}-${index}`} style={{ display: 'flex', marginBottom: 6, fontSize: 13 }}>
                                                <div style={{ minWidth: 110, color: '#595959' }}>{row.title}</div>
                                                <div style={{ fontWeight: 600 }}>{row.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {livePreviewData?.buttons?.length > 0 ? (
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {livePreviewData.buttons.map((button: any, index: number) => (
                                            <Button key={`${button.title}-${index}`} type="primary" block>
                                                {button.title}
                                            </Button>
                                        ))}
                                    </Space>
                                ) : (
                                    <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                                        Chưa có nút thao tác trong template.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

        </AdminLayout>
    );
};

export default ZaloTemplateManagement;
